import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { ModalService } from '../../../core/services/modal.service';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { NumberInputComponent } from '../../../shared/ui/forms/number-input/number-input.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { LoadService } from '../../../core/services/load.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { LoadWithView } from '../../../core/models/load.model';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { DatePickerComponent } from '../../../shared/ui/forms/date-picker/date-picker.component';
import { Timestamp } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { computeLoadLineItem } from '../../../shared/utilities/invoice-pricing';

@Component({
  selector: 'app-mark-paid-modal',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule,
    LucideIconComponent,
    ButtonComponent,
    NumberInputComponent,
    InputComponent,
    TextComponent,
    FormFieldComponent,
    DatePickerComponent
  ],
  templateUrl: './mark-paid-modal.component.html',
})
export class MarkPaidModalComponent {
  private fb = inject(FormBuilder);
  private loadService = inject(LoadService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private modalService = inject(ModalService);

  protected readonly load = (this.modalService.state() as any)?.data?.load as LoadWithView;
  protected readonly onSuccess = (this.modalService.state() as any)?.data?.onSuccess;
  protected readonly totalAmount = this.computeLoadTotal();

  form: FormGroup;
  isSaving = signal(false);

  constructor() {
    this.form = this.fb.group({
      amount: [this.totalAmount, [Validators.required, Validators.min(0.01)]],
      invoiceNumber: [''],
      paymentMethod: [''],
      paymentDate: [new Date(), [Validators.required]]
    });
  }

  private computeLoadTotal(): number {
    if (!this.load) return 0;
    const view = this.load.view;
    const lineItem = computeLoadLineItem(
      view?.pricing,
      (view as any)?.bookedAtRate,
      view?.tonuDecision
    );
    return lineItem.total || view?.pricing?.totalAmount || 0;
  }

  private toPaymentTimestamp(value: any): Timestamp {
    if (!value) return Timestamp.now();
    if (value instanceof Timestamp) return value;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? Timestamp.now() : Timestamp.fromDate(value);
    }
    if (typeof value?.toJSDate === 'function') {
      const jsDate = value.toJSDate();
      return jsDate instanceof Date && !isNaN(jsDate.getTime())
        ? Timestamp.fromDate(jsDate)
        : Timestamp.now();
    }
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      return d instanceof Date && !isNaN(d.getTime())
        ? Timestamp.fromDate(d)
        : Timestamp.now();
    }
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return Timestamp.fromDate(new Date(year, month - 1, day, 12, 0, 0));
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? Timestamp.now() : Timestamp.fromDate(parsed);
    }
    return Timestamp.now();
  }

  close() {
    this.modalService.close();
  }

  async save() {
    if (this.form.invalid || this.isSaving()) return;
    
    const companyId = this.authService.activeCompanyId();
    if (!companyId) return;

    this.isSaving.set(true);

    try {
      const { amount, invoiceNumber, paymentMethod, paymentDate } = this.form.value;
      
      const updateData: any = {
        billingStatus: 'PAID',
        paidAmount: amount,
        paidAt: this.toPaymentTimestamp(paymentDate),
        'pricing.paymentStatus': 'paid',
        'pricing.paidAmount': amount,
        'pricing.status': 'paid'
      };

      if (invoiceNumber) {
        updateData.invoiceNumber = invoiceNumber;
      }
      
      if (paymentMethod) {
        updateData['pricing.paymentNotes'] = `Method: ${paymentMethod}`;
        updateData.billingNotes = `Method: ${paymentMethod}`;
      }

      await firstValueFrom(this.loadService.patchCompanyView(this.load.id, companyId, updateData));

      this.toastService.success('Load marked as paid');
      this.close();
      if (this.onSuccess) this.onSuccess();
    } catch (error: any) {
      console.error('Error marking load as paid:', error);
      this.toastService.error(error.message || 'Failed to mark load as paid');
    } finally {
      this.isSaving.set(false);
    }
  }
}
